import {
  NOT,
  YES
} from '../../../lib/Boolean.lib';
import {
  getPersonName
} from '../../../lib/Object.lib';
import evalCandidateARating from '../../../lib/services/Filtering/evalCandidateARating.fun';
import evalCandidateEngsAllReview from '../../../lib/services/Filtering/evalCandidateEngsAllReview.fun';
import evalCandidateMatchExclusion from '../../../lib/services/Filtering/evalCandidateMatchExclusion.fun';

/**
 * Evaluates whether a candidate should be considered valid for a given job
 * in the matching process.
 *
 * @function evalJobCandidate
 * @param {Object} params - Function parameters.
 * @param {Object} params.candidate - Candidate object with engagements
 *   and rating data.
 * @param {boolean} [params.extendedFilter=false] - Flag indicating whether
 *   extended filtering rules should apply.
 * @returns {boolean} True if the candidate passes filtering criteria.
 *
 * @description
 * - Excludes candidates flagged with match exclusions.
 * - When `extendedFilter` is **disabled**:
 *   - Candidate passes if not excluded.
 * - When `extendedFilter` is **enabled**:
 *   - Candidate must satisfy one of:
 *     - Not all candidate engagements are at `review` stage (meaning the
 *       candidate has progressed to interview or beyond).
 *     - Candidate has an "A" or "A+" platform rating.
 * - Currently, visa and location matching checks are commented out
 *   (see tweak: `2025-09-04 sc-13100`) but can be re-enabled if required.
 * - Intended for use in candidate-job filtering pipelines.
 */
export default function evalJobCandidate({
  candidate,
  extendedFilter = false
}) {
  const YES__EXTENDED__FILTER = YES(extendedFilter);
  const NOT__EXTENDED__FILTER = NOT(YES__EXTENDED__FILTER);
  const YES__NOT_MATCH__EXCLUSION = evalCandidateMatchExclusion(candidate);
  const YES__ALL__ENGAGEMENTS__REVIEW = evalCandidateEngsAllReview({
    candidateId: candidate.id,
    engagements: candidate.ENGAGEMENTS_SUBMITTED
  });
  // if NOT all engagements are in review or hasn't engagements
  // then the candidate has at least once reached an interview stage
  const YES__ENGAGEMENTS_PASSED_REVIEW = NOT(YES__ALL__ENGAGEMENTS__REVIEW);
  const YES__CANDIDATE__HAS__A_RATING = evalCandidateARating(candidate);
  const result = (
    // Exclude candidates flagged for exclusion
    YES__NOT_MATCH__EXCLUSION
    && (
      // AND the extended filter is NOT enabled,
      NOT__EXTENDED__FILTER
      // OR the extended filter is enabled AND,
      || (
        (
          // candidate has engagements
          // and at least one engagement
          // reached a interview stage
          YES__ENGAGEMENTS_PASSED_REVIEW
          // OR candidate has A or A+ rating
          || YES__CANDIDATE__HAS__A_RATING
        )
        // These checks matching against the job matching profile,
        // not the job selected by the job dropdown
        /**
         * @tweak 2025-09-04 sc-13100
         * Comment out the visa and location filtering.
         * Leave a comment to potentially re-enable in the future.
         */
        // && evalVisaMatch({ candidate, job: MatchPage.state.profile })
        // && evalCandidateJobLocation({ candidate, job: MatchPage.state.profile })

      )
    )
  );
  console.debug(
    'evalJobCandidate', result,
    getPersonName(candidate) + '\n',
    {
      YES__EXTENDED__FILTER,
      YES__NOT_MATCH__EXCLUSION,
      YES__ENGAGEMENTS_PASSED_REVIEW,
      YES__CANDIDATE__HAS__A_RATING,
      candidate,
    }
  );
  return result;
}
